import { useEffect, useState } from "react";
import { GradingData } from "../../../views/GradingCenterView";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import { Alert, StyleSheet, View } from "react-native";

import SubmissionAndGradingTab from "./SubmissionAndGradingTab";
import AttemptHistoryTab from "./AttemptHistoryTab";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { useAppConfig } from "../../../AppConfigProvider";
import TabNavigator from "../../../components/customCoursework/GradeModalTabNavigator/TabNavigator";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { DEFAULT_QUESTIONS_COUNT } from "../../../constants/assessmentConstants";

const AssessmentGradingModalContainer = ({
  isVisible,
  onClose,
  gradingData,
  isLoading,
  canEditScore,
  onSave,
  isSubmitting,
  updateError,
}: {
  isVisible: boolean;
  onClose: () => void;
  gradingData: GradingData | null;
  isLoading: boolean;
  canEditScore: boolean;
  onSave?: (gradingData: GradingData, newScore: number) => Promise<void>;
  isSubmitting?: boolean;
  updateError?: string | null;
}) => {
  const [activeTab, setActiveTab] = useState<string>("submissionAndGrading");
  const [grade, setGrade] = useState<string>("");

  const appConfig = useAppConfig();
  const primaryColor = appConfig.primaryColor;

  const handleTabPress = (tab: string) => {
    setActiveTab(tab);
  };

  const numberOfAnsweredQuestions =
    gradingData?.questions?.filter((question) => question?.userAnswer?.answer).length ?? DEFAULT_QUESTIONS_COUNT;

  const handleGradeChange = (text: string) => {
    const newScore = Number(text);

    if (Number.isNaN(newScore)) {
      Alert.alert("Invalid score", "Please enter a valid number");
      return;
    }

    if (newScore < 0 || newScore > numberOfAnsweredQuestions * 10) {
      Alert.alert("Invalid score", "Please enter a score between 0 and 100");
      return;
    }

    setGrade(newScore.toString());
  };

  useEffect(() => {
    if (gradingData?.totalPoints) {
      setGrade(gradingData.totalPoints.toString());
    }
  }, [gradingData]);

  const handleSave = async () => {
    const newScore = Number(grade);

    if (newScore < 0 || newScore > numberOfAnsweredQuestions * 10) {
      Alert.alert("Invalid score", "Please enter a score between 0 and 100");
      return;
    }

    if (Number.isNaN(newScore)) {
      Alert.alert("Invalid score", "Please enter a valid number");
      return;
    }

    await onSave(gradingData, newScore);
  };

  return (
    <CustomModal
      visible={isVisible}
      onRequestClose={onClose}
      style={styles.modalContent}
      onShow={() => {}}
      ariaLabel="Assessment Responses Modal">
      <View style={styles.header}>
        <CustomText style={styles.title} text="Grade Assessment" />
      </View>

      <CustomSpacing size="xl" />
      <TabNavigator
        tabData={[
          {
            key: "submissionAndGrading",
            title: "Submission & Grading",
          },
          {
            key: "attemptHistory",
            title: "Attempt History",
          },
        ]}
        activeTab={activeTab}
        onTabPress={handleTabPress}
        activeTabColor={primaryColor}
        rightButtonComponent={null}
      />
      {activeTab === "submissionAndGrading" && (
        <SubmissionAndGradingTab
          gradingData={gradingData}
          isLoading={isLoading}
          handleGradeChange={handleGradeChange}
          grade={grade}
          canEditScore={canEditScore}
        />
      )}
      {activeTab === "attemptHistory" && (
        <AttemptHistoryTab assessmentId={gradingData?.assessmentId} userId={gradingData?.userId} />
      )}

      {canEditScore && updateError && (
        <View style={styles.errorContainer}>
          <CustomText text={updateError} size="s" useTranslationText={false} style={{ color: "red" }} />
        </View>
      )}

      <View style={styles.modalFooter}>
        <View style={styles.footerButtons}>
          <CustomButton
            text={canEditScore ? "Cancel" : "Close"}
            styleType={"primaryTransparentStroke"}
            onPress={onClose}
            style={{ borderWidth: 2, borderColor: primaryColor, paddingHorizontal: 16, paddingVertical: 8 }}
            textStyle={{ color: primaryColor, fontWeight: 700 }}
          />
          {canEditScore && (
            <CustomButton text="Save" styleType="primary" onPress={handleSave} disabled={isSubmitting} />
          )}
        </View>
      </View>
    </CustomModal>
  );
};

export default AssessmentGradingModalContainer;

const styles = StyleSheet.create({
  modalContent: {
    maxHeight: "90%",
    minWidth: "90%",
    width: "90%",
    padding: 0,
  },
  modalFooter: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 16,
    marginTop: 24,
    paddingTop: 16,
    paddingHorizontal: 32,
    paddingBottom: 40,
  },
  footerButtons: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 8,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 32,
    backgroundColor: "#F2F4F7",
    width: "100%",
    alignSelf: "stretch",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  title: {
    fontSize: 20,
    fontFamily: "Inter",
    fontWeight: "600",
    color: "#344054",
  },
  errorContainer: {
    paddingHorizontal: 32,
    paddingBottom: 16,
  },
});
